import React, { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

// Soft-delete: re-type engagement name to confirm. Recoverable.
export function EngagementDeleteDialog({ eng, onOpenChange, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(""); }, [eng]);
  if (!eng) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/m1/engagements/${eng.id}/delete`, { confirm_name: name });
      toast.success("Engagement deleted (recoverable from the Deleted tab)");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!eng} onOpenChange={onOpenChange}>
      <DialogContent data-testid="eng-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete engagement</DialogTitle>
          <DialogDescription>This hides the engagement and revokes access. Nothing is permanently erased — it stays recoverable indefinitely in the Deleted tab.</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Type <span className="font-semibold">{eng.name}</span> to confirm</Label>
          <Input data-testid="eng-delete-confirm" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="eng-delete-submit" disabled={busy || name !== eng.name} onClick={submit} className="bg-red-600 hover:bg-red-700">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Permanent delete: irreversible. Tenant Admin only. Two separate confirmations.
export function PermanentDeleteDialog({ eng, onOpenChange, onDone }) {
  const [name, setName] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(""); setPhrase(""); }, [eng]);
  if (!eng) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/m1/engagements/${eng.id}/permanent-delete`, { confirm_name: name, confirm_phrase: phrase });
      toast.success("Engagement permanently deleted");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!eng} onOpenChange={onOpenChange}>
      <DialogContent data-testid="eng-perm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Permanently delete</DialogTitle>
          <DialogDescription>This erases the engagement and every request, comment and file on it forever. This cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type <span className="font-semibold">{eng.name}</span> to confirm</Label>
            <Input data-testid="eng-perm-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label>Then type <span className="font-semibold">DELETE FOREVER</span></Label>
            <Input data-testid="eng-perm-phrase" className="mt-1.5" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="DELETE FOREVER" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="eng-perm-submit" disabled={busy || name !== eng.name || phrase.trim().toUpperCase() !== "DELETE FOREVER"} onClick={submit} className="bg-red-600 hover:bg-red-700">Delete forever</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
